import { useEffect, useRef, useState } from "react";
import { Button, Icon } from "../../components/design";
import styles from "./PermisssionWidget.module.css";

// The widget is on Permisso's site, so we cannot measure it. If it tells us
// its height in a message, the frame takes that height (no inner scroll).
// ponytail: Permisso's message format is not documented; these are the usual
// shapes. Check the dev console log below and keep only the real one.
const readHeight = (data) => {
  const value = Number(
    data?.height ?? data?.data?.height ?? data?.payload?.height
  );
  return Number.isFinite(value) && value > 0 ? value : null;
};

// keeps a broken or looping message from making a giant or tiny frame
const MIN_HEIGHT = 320;
const MAX_HEIGHT = 4000;

const PermissoWidget = ({ widgetUrl, onClose, onSuccess, onError }) => {
  const [frameHeight, setFrameHeight] = useState(null);
  const pendingHeight = useRef(null);
  const frameRequest = useRef(0);

  useEffect(() => () => cancelAnimationFrame(frameRequest.current), []);

  useEffect(() => {
    const handleMessage = (event) => {
      // Allowing all origins on dev only
      const allowedOrigin = [
        "https://widget.bankflip.io",
        "https://widget.permisso.io",
        "https://widget.sandbox.bankflip.io",
        "https://widget.sandbox.permisso.io",
      ];

      if (!allowedOrigin.includes(event?.origin)) {
        return;
      }

      const data = event?.data;

      if (process.env.NODE_ENV === "development") {
        console.debug("[Permisso widget message]", data);
      }

      const height = readHeight(data);
      if (height) {
        // at most one update per frame, and only for a real change
        pendingHeight.current = Math.min(
          Math.max(Math.ceil(height), MIN_HEIGHT),
          MAX_HEIGHT
        );
        if (!frameRequest.current) {
          frameRequest.current = requestAnimationFrame(() => {
            frameRequest.current = 0;
            const next = pendingHeight.current;
            setFrameHeight((current) =>
              current !== null && Math.abs(current - next) < 4 ? current : next
            );
          });
        }
        return;
      }

      if (data?.name === "user_requested_closure") {
        onSuccess && onSuccess(data);
        onClose && onClose();
      } else if (
        ["session_cancelled", "token_expired", "session_expired"].includes(
          data?.name
        )
      ) {
        onError && onError(data);
        onClose && onClose();
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [onClose, onSuccess, onError]);

  return (
    <div
      className={`popup-overlay wrapper-modal modal-fixed-screen ${styles.widgetOverlay}`}
    >
      <div className={styles.widgetCard}>
        <iframe
          src={widgetUrl}
          className={styles.widgetFrame}
          style={frameHeight ? { height: frameHeight } : undefined}
          scrolling={frameHeight ? "no" : undefined}
          title="Permisso Widget"
          allow="camera; microphone; clipboard-read; clipboard-write"
        />
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          className={styles.closeButton}
          iconStart={<Icon name="close" size={18} />}
          onClick={() => onClose && onClose()}
          aria-label="Close"
        />
      </div>
    </div>
  );
};

export default PermissoWidget;
